import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Search,
  WalletCards,
} from "lucide-react";
import PayoutReportTable, {
  PayoutReportRecord,
} from "./PayoutReportTable";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import { useAuth } from "../../../../context/AuthContext";
import "./PayoutReportPage.css";

type PayoutReportApiRow = {
  month: string;
  year: number;
  referralCommissions: number;
  tdsAmount: number;
  payoutCount: number;
  netPayableAmount: number;
  tdsCertificate?: string;
};

type PayoutReportResponse = {
  customerId: string;
  customerName: string;
  totalMonths: number;
  totalReferralCommissions: number;
  totalTdsAmount: number;
  data: PayoutReportApiRow[];
  paging: {
    total: number;
    page: number;
    limit: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
};

const formatCurrency = (value: number) =>
  `₹${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`;

const PayoutReport = () => {
  const { user } = useAuth();

  const [report, setReport] = useState<PayoutReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [entries, setEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, entries, dateRange]);

  useEffect(() => {
    if (!user?._id) return;
    if (dateRange.from && !dateRange.to) return;

    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_PAYOUT_PERFIX}/customer-reports/customer/${user._id}/payout-report`,
          {
            params: {
              page: currentPage,
              limit: entries,
              search: debouncedSearch || undefined,
              dateFrom: dateRange.from || undefined,
              dateTo: dateRange.to || undefined,
            },
            withCredentials: true,
          },
        );
        setReport(response.data);
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            "Failed to fetch payout report",
        );
        setReport(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [
    user?._id,
    currentPage,
    entries,
    debouncedSearch,
    dateRange.from,
    dateRange.to,
  ]);

  const records: PayoutReportRecord[] = useMemo(
    () =>
      (report?.data || []).map((row, index) => ({
        id: `${row.year}-${row.month}-${index}`,
        month: row.month,
        year: row.year,
        payoutCount: row.payoutCount,
        receivedReferralIncentive: row.referralCommissions,
        tdsPercentage:
          row.referralCommissions > 0
            ? Math.round((row.tdsAmount / row.referralCommissions) * 100)
            : 0,
        tdsAmount: row.tdsAmount,
      })),
    [report],
  );

  const totalReceived = report?.totalReferralCommissions ?? 0;
  const totalTds = report?.totalTdsAmount ?? 0;
  const netPayout = totalReceived - totalTds;
  const totalRecords = report?.paging?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / entries));

  const resultStart =
    totalRecords === 0 ? 0 : (currentPage - 1) * entries + 1;
  const resultEnd = Math.min(currentPage * entries, totalRecords);

  return (
    <section className="payout-report-page">
      <div className="payout-report-shell">
        <header className="payout-report-header">
          <div className="payout-report-heading">
            <div className="payout-report-heading-icon">
              <FileText size={22} strokeWidth={2.15} />
            </div>

            <div className="payout-report-heading-copy">
              <span className="payout-report-eyebrow">
                Withdrawal
              </span>
              <h1>Payout Report</h1>
              <p>
                Review referral incentive payouts, TDS deductions
                and payment status.
              </p>
            </div>
          </div>

          <div className="payout-report-stats">
            <div className="payout-report-stat">
              <span>Total Received</span>
              <strong>{formatCurrency(totalReceived)}</strong>
            </div>
            <div className="payout-report-stat">
              <span>Total TDS</span>
              <strong>{formatCurrency(totalTds)}</strong>
            </div>
            <div className="payout-report-stat payout-report-stat-primary">
              <span>Net Payout</span>
              <strong>{formatCurrency(netPayout)}</strong>
            </div>
          </div>
        </header>

        <div className="payout-report-toolbar">
          <div className="payout-report-toolbar-left">
            <select
              aria-label="Entries per page"
              value={entries}
              onChange={(event) =>
                setEntries(Number(event.target.value))
              }
            >
              {[5, 10, 20].map((option) => (
                <option key={option} value={option}>
                  {option} Entries
                </option>
              ))}
            </select>

            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
            />

            <span className="payout-report-record-count">
              {totalRecords} records
            </span>
          </div>

          <label className="payout-report-search">
            <Search size={17} strokeWidth={2.2} />
            <input
              type="search"
              value={search}
              placeholder="Search payouts..."
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>

        <PayoutReportTable
          records={records}
          resultStart={resultStart}
          loading={loading}
          error={error}
        />

        <footer className="payout-report-footer">
          <div className="payout-report-result-text">
            Showing <strong>{resultStart}</strong> -{" "}
            <strong>{resultEnd}</strong> of{" "}
            <strong>{totalRecords}</strong>
          </div>

          <div className="payout-report-pagination">
            <button
              type="button"
              aria-label="Previous page"
              disabled={currentPage === 1}
              onClick={() =>
                setCurrentPage((previous) =>
                  Math.max(1, previous - 1),
                )
              }
            >
              <ChevronLeft size={17} />
            </button>

            {Array.from(
              { length: totalPages },
              (_, index) => index + 1,
            ).map((pageNumber) => (
              <button
                type="button"
                key={pageNumber}
                className={
                  pageNumber === currentPage ? "active" : ""
                }
                onClick={() => setCurrentPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}

            <button
              type="button"
              aria-label="Next page"
              disabled={currentPage === totalPages}
              onClick={() =>
                setCurrentPage((previous) =>
                  Math.min(totalPages, previous + 1),
                )
              }
            >
              <ChevronRight size={17} />
            </button>
          </div>
        </footer>

        <div className="payout-report-note">
          <WalletCards size={16} strokeWidth={2.2} />
          <span>
            Net payout is calculated as total received incentive
            minus TDS deduction.
          </span>
        </div>
      </div>
    </section>
  );
};

export default PayoutReport;
